import { IssueCustomAttributeType } from "./IssueCustomAttributeDefinition";
import { PermittedAction } from "./common";
import { IssuePlacementPin } from "./IssuePlacementPin";
import { WatcherObject } from "./WatcherObject";
import { OriginContextPayload, OriginContext } from "./OriginContext";

export interface IssueLinkedDocument {
  type: "TwoDVectorPushpin" | "TwoDRasterPushpin";
  urn: string;
  createdBy?: string;
  createdAt?: string;
  createdAtVersion: number;
  closedBy?: string;
  closedAt?: string;
  closedAtVersion?: string;
  details: {
    viewable?: {
      id?: string;
      viewableId?: string;
      guid?: string;
      name: string;
      is3D: boolean;
    };
    position: {
      x: number;
      y: number;
      z?: number;
    };
    objectId?: number;
    viewerState?: any;
    externalId?: string;
    attributesVersion?: number;
    urn?: string;
  };
}

export interface IssueLinkedDocumentPayload extends IssueLinkedDocument {
  originContext?: OriginContextPayload;
}

export interface IssueLink {
  type: "checklist";
  data: { [key: string]: string };
}

export interface IssueCustomAttribute {
  attributeDefinitionId: string;
  value: string | number | boolean;
  type?: IssueCustomAttributeType;
  title?: string;
}

export enum IssueStatus {
  Draft = "draft",
  Open = "open",
  Pending = "pending",
  InProgress = "in_progress",
  Completed = "completed",
  InReview = "in_review",
  NotApproved = "not_approved",
  InDispute = "in_dispute",
  Closed = "closed",
}

export interface IssueAttributes {
  id?: string;
  title: string;
  description?: string;
  status: IssueStatus;
  assignedTo?: string | null;
  assignedToType?: string | null;
  startingVersion?: string;
  startDate?: string;
  dueDate?: string;
  locationDetails?: string;
  locationId?: string | null;
  issueTypeId: string;
  rootCauseId?: string;
  linkedDocuments?: IssueLinkedDocumentPayload[];
  ownerId?: string;
  published?: boolean;
  watchers?: string[];
  watcherObjects?: WatcherObject[];
  customAttributes?: IssueCustomAttribute[];
  gpsCoordinates?: GpsCoordinates;
  placements?: IssuePlacementWithPins[];
  skipThumbnailGeneration?: boolean;
}

export type IssuePermittedAction =
  | PermittedAction.Delete
  | PermittedAction.Undelete
  | PermittedAction.CanEdit
  | PermittedAction.AddComment
  | PermittedAction.AddAttachment
  | PermittedAction.RemoveAttachment
  | PermittedAction.ClearAssignee
  | PermittedAction.AssignAll
  | PermittedAction.AssignSameCompany
  | PermittedAction.Restore
  | PermittedAction.UpsertPin
  | PermittedAction.UnlinkPin
  | PermittedAction.Copy;

export interface Issue {
  id: string;
  projectId: string;
  displayId: string;
  title?: string;
  description?: string;
  snapshotUrn?: string;
  snapshotHasMarkups?: boolean;
  issueTypeId?: string;
  issueTypeCategoryId?: string;
  status?: IssueStatus;
  assignedTo?: string | null;
  assignedToType?: string | null;
  startDate?: string;
  dueDate?: string;
  locationId?: string | null;
  locationDetails?: string;
  linkedDocuments: IssueLinkedDocument[];
  placements: IssuePlacement[];
  links: IssueLink[];
  ownerId?: string;
  rootCauseId?: string;
  officialResponse?: {
    response: string;
    respondedAt: string;
    respondedBy: string;
  };
  issueTemplateId?: string;
  permittedStatuses: IssueStatus[];
  permittedAttributes: string[];
  published: boolean;
  deleted: boolean;
  permittedActions: IssuePermittedAction[];
  commentCount: number;
  attachmentCount: number;
  openedBy?: string;
  openedAt?: string;
  closedBy?: string;
  closedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  watchers?: string[];
  watcherObjects?: WatcherObject[];
  customAttributes: IssueCustomAttribute[];
  gpsCoordinates?: GpsCoordinates;
  deletedBy?: string;
  deletedAt?: string;
}

export enum IssueSortingField {
  DisplayId = "displayId",
  Title = "title",
  Description = "description",
  Status = "status",
  AssignedToType = "assignedToType",
  DueDate = "dueDate",
  StartDate = "startDate",
  LocationDetails = "locationDetails",
  ClosedAt = "closedAt",
  CreatedAt = "createdAt",
  ClientCreatedAt = "clientCreatedAt",
  UpdatedAt = "updatedAt",
  OpenedAt = "openedAt",
  DeletedBy = "deletedBy",
  DeletedOn = "deletedAt",
  RootCause = "rootCause",
  IssueType = "issueSubType",
  IssueTypeCategory = "issueType",
  Published = "published",
}

export enum IssueSortingDirection {
  Asc = "asc",
  Desc = "desc",
}

export interface IssueSort {
  /**
   * The attribute to sort by
   */
  attribute: IssueSortingField;
  /**
   * The direction of the sort. Default is Asc.
   */
  direction?: IssueSortingDirection;
}

/**
 * DateFilter can be an ISO string of a date, format YYYY-MM-DD, an ISO String date and time, or null
 */
export type DateFilter = Date | string | null;

export type DateRange = {
  startDate: DateFilter;
  endDate: DateFilter;
};

export type EntityIdOrList = string | string[];

export type CustomAttributeFilter = {
  [id: string]: string | number | boolean | string[];
};

export type IssueFilterField = keyof IssueFilter;

export const IssueDateFilters: IssueFilterField[] = [
  "dueDate",
  "startDate",
  "createdAt",
  "openedAt",
  "updatedAt",
  "closedAt",
];

export interface ViewableInformation {
  /**
   * Lineage urn (i.e 'urn:adsk.wipqa:dm.lineage:x7HERgYxSdyRiKPBdoeq2Q')
   */
  lineageUrn: string;
  /**
   * internal WipDM version number
   */
  versionNumber: number;
  viewableId?: string;
}

export interface ViewableCriterion {
  guid: string;
  viewableId: string;
  name: string;
  // Accept lineageUrn for pairing document and viewable
  lineageUrn?: string;
}

export interface IssueFilter {
  id?: EntityIdOrList;
  issueTypeCategoryId?: EntityIdOrList;
  issueTypeId?: EntityIdOrList;
  status?: IssueStatus | IssueStatus[] | null;
  //@deprecated Use placements.lineageUrn instead
  linkedDocumentUrn?: EntityIdOrList;
  "placements.lineageUrn"?: EntityIdOrList;
  //@deprecated experimental
  "placements.sheetId"?: EntityIdOrList;
  dueDate?: DateRange;
  startDate?: DateRange;
  createdAt?: DateRange;
  createdBy?: EntityIdOrList;
  openedAt?: DateRange;
  openedBy?: EntityIdOrList;
  updatedAt?: DateRange;
  updatedBy?: EntityIdOrList;
  assignedTo?: EntityIdOrList;
  "assignedTo.includeMembers"?: boolean;
  rootCauseId?: EntityIdOrList;
  // TODO: after we remove the multi-location filter FF, we should change these to string[]
  locationId?: EntityIdOrList;
  subLocationId?: EntityIdOrList;
  closedBy?: EntityIdOrList;
  closedAt?: DateRange;
  title?: string;
  search?: string;
  displayId?: EntityIdOrList;
  customAttributes?: CustomAttributeFilter;
  valid?: boolean;
  published?: boolean;
  watchers?: string[];
  "watchers.includeMembers"?: boolean;
  viewable?: ViewableCriterion[];
  gpsCoordinates?: boolean;
  deleted?: boolean;
}

export interface IssueViewers {
  usersOxygenIds: string[];
  rolesOxygenIds: string[];
  companiesOxygenIds: string[];
}

export interface IssueFailureInfo {
  issueUid: string;
  statusCode: number;
}

export interface IssuesFetchParams {
  limit: number;
  offset: number;
  sortBy: IssueSort;
  filterBy: IssueFilter;
}

export interface LatLng {
  latitude: number;
  longitude: number;
}

export enum IssuePlacementType {
  FILE = "file",
  SHEET = "sheet",
}

interface SheetViewable {
  guid: string;
  name: string;
}

export interface FileViewable {
  viewableId: string;
  guid: string;
  name: string;
  is3D: boolean;
}

interface IssuePlacementBase {
  type: IssuePlacementType;
  viewable: SheetViewable | FileViewable;
  originContext?: OriginContext;
}

export interface IssueSheetPlacement extends IssuePlacementBase {
  type: IssuePlacementType.SHEET;
  viewable: SheetViewable;
  sheetId: string;
}

export interface IssueFilePlacement extends IssuePlacementBase {
  type: IssuePlacementType.FILE;
  viewable: FileViewable;
  lineageUrn: string;
  createdAtVersion: number;
}

export type IssuePlacement = IssueSheetPlacement | IssueFilePlacement;

export type IssuePlacementWithPins = IssuePlacement & {
  pins: IssuePlacementPin[];
  originContext?: OriginContextPayload;
};

export type GpsCoordinates = LatLng | null | undefined;
